/**
 * Personas Dashboard
 *
 * Overview page for persona management showing:
 * - Total personas count
 * - Generation mode indicator
 * - Recent personas list
 * - Quick links to Browse, Configuration
 */

import { Link } from 'react-router-dom'
import { User, Settings, Grid, RefreshCw, Clock, TrendingUp } from 'lucide-react'
import { usePersonas, usePersonaConfiguration } from '@/hooks/usePersonas'
import PersonaCard from '@/components/personas/PersonaCard'
import { ModeBadge } from '@/components/personas/PersonaModeSelector'

export default function PersonasDashboard() {
  const { data: personasData, isLoading: personasLoading } = usePersonas({ limit: 10 })
  const { data: config, isLoading: configLoading } = usePersonaConfiguration()

  const isLoading = personasLoading || configLoading

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-gray-200 rounded mb-2" />
          <div className="h-4 w-64 bg-gray-200 rounded" />
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card p-6 animate-pulse">
              <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
              <div className="h-8 w-16 bg-gray-200 rounded mb-2" />
              <div className="h-4 w-32 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  const totalPersonas = personasData?.total || 0
  const recentPersonas = personasData?.personas || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Personas</h2>
        <p className="text-gray-600 mt-1">
          AI-optimized counterparty briefings for personalized interactions
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-personic-100 rounded-lg">
              <User className="w-5 h-5 text-personic-600" />
            </div>
            <div className="text-sm text-gray-500">Total Personas</div>
          </div>
          <div className="text-3xl font-bold text-gray-900">{totalPersonas}</div>
          <p className="text-sm text-gray-600 mt-2">
            Across all profiles
          </p>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-personic-100 rounded-lg">
              <Settings className="w-5 h-5 text-personic-600" />
            </div>
            <div className="text-sm text-gray-500">Default Mode</div>
          </div>
          <div className="flex items-center gap-2 mt-1">
            {config && <ModeBadge mode={config.default_generation_mode} />}
            <span className="text-sm text-gray-600">generation</span>
          </div>
          <p className="text-sm text-gray-600 mt-3">
            {config?.auto_generate ? 'Auto-generation enabled' : 'Manual generation only'}
          </p>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-personic-100 rounded-lg">
              <Clock className="w-5 h-5 text-personic-600" />
            </div>
            <div className="text-sm text-gray-500">TTL Setting</div>
          </div>
          <div className="text-3xl font-bold text-gray-900">
            {config?.ttl_hours || 24}h
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {config?.refresh_on_profile_change ? 'Auto-refresh on changes' : 'Manual refresh only'}
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-6">
        <Link
          to="/tenant/personas/browse"
          className="card p-6 hover:shadow-md transition-shadow group"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-personic-100 rounded-lg text-personic-600 group-hover:bg-personic-200 transition-colors">
              <Grid className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Browse Personas</h3>
              <p className="text-sm text-gray-600">
                Search and filter all generated personas
              </p>
            </div>
          </div>
        </Link>

        <Link
          to="/tenant/personas/configuration"
          className="card p-6 hover:shadow-md transition-shadow group"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-personic-100 rounded-lg text-personic-600 group-hover:bg-personic-200 transition-colors">
              <Settings className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Configuration</h3>
              <p className="text-sm text-gray-600">
                Manage generation settings and defaults
              </p>
            </div>
          </div>
        </Link>

        <Link
          to="/tenant/profiles"
          className="card p-6 hover:shadow-md transition-shadow group"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-personic-100 rounded-lg text-personic-600 group-hover:bg-personic-200 transition-colors">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">View Profiles</h3>
              <p className="text-sm text-gray-600">
                Generate personas from profiles
              </p>
            </div>
          </div>
        </Link>
      </div>

      {/* Recent Personas */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Recent Personas</h3>
          <Link
            to="/tenant/personas/browse"
            className="text-sm text-personic-600 hover:text-personic-700"
          >
            View all
          </Link>
        </div>
        <div className="p-6">
          {recentPersonas.length === 0 ? (
            <div className="text-center py-8">
              <User className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h4 className="text-gray-900 font-medium mb-2">No personas yet</h4>
              <p className="text-gray-500 text-sm mb-4">
                Generate personas from your profiles to see them here.
              </p>
              <Link
                to="/tenant/profiles"
                className="inline-flex items-center gap-2 px-4 py-2 bg-personic-600 text-white rounded-lg hover:bg-personic-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Go to Profiles
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentPersonas.slice(0, 6).map((persona) => (
                <PersonaCard key={persona.persona_id} persona={persona} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Usage Tips */}
      <div className="card p-6 bg-gradient-to-r from-personic-50 to-white">
        <h3 className="font-semibold text-gray-900 mb-3">About Personas</h3>
        <div className="grid md:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-medium text-gray-800 mb-1">What are Personas?</h4>
            <p className="text-gray-600">
              Personas are AI-agent-optimized briefings generated from Personic profiles,
              designed to help AI agents personalize interactions.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-800 mb-1">Generation Modes</h4>
            <p className="text-gray-600">
              Choose between Full (all dimensions), Partial (functional only), or
              Minimal (communication style) based on your compliance requirements.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-800 mb-1">Sensitivity</h4>
            <p className="text-gray-600">
              Personas respect sensitivity settings to ensure regulatory compliance
              for clinical, legal, and educational contexts.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
